import { createSlice } from '@reduxjs/toolkit';
import { fetchWorkflowTagsBootstrap } from './workflowTagsThunks';

/** Bump when `tagsByWorkflowId` / workflow tags payload shape changes. */
export const WORKFLOW_TAGS_PAYLOAD_VERSION = 1;

/**
 * @typedef {{ tag_id: number | string, tag_name: string, workflow_id: string }} WorkflowTagRow
 */

function getInitialState() {
  return {
    version: null,
    updatedAt: null,
    /** @type {Record<string, WorkflowTagRow[]>} */
    tagsByWorkflowId: {},
    workflowIds: [],
    /** Raw rows from `getGlobalWorkflows` (workflow metadata). */
    globalWorkflows: [],
    isLoading: false,
    error: null,
  };
}

const workflowTagsSlice = createSlice({
  name: 'workflowTags',
  initialState: getInitialState(),
  reducers: {
    setWorkflowTagsPayload: (state, action) => {
      const {
        tagsByWorkflowId,
        workflowIds,
        globalWorkflows,
        version,
        updatedAt,
      } = action.payload;
      state.version = version ?? WORKFLOW_TAGS_PAYLOAD_VERSION;
      state.updatedAt = updatedAt ?? new Date().toISOString();
      state.tagsByWorkflowId =
        tagsByWorkflowId && typeof tagsByWorkflowId === 'object'
          ? tagsByWorkflowId
          : {};
      state.workflowIds = Array.isArray(workflowIds)
        ? workflowIds
        : Object.keys(state.tagsByWorkflowId);
      if (globalWorkflows !== undefined) {
        state.globalWorkflows = Array.isArray(globalWorkflows)
          ? globalWorkflows
          : [];
      }
      state.error = null;
    },
    setWorkflowTagsLoading: (state, action) => {
      state.isLoading = Boolean(action.payload);
    },
    setWorkflowTagsError: (state, action) => {
      state.error =
        action.payload == null || action.payload === ''
          ? null
          : String(action.payload);
    },
    clearWorkflowTags: () => getInitialState(),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowTagsBootstrap.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowTagsBootstrap.fulfilled, (state, action) => {
        state.isLoading = false;
        const { tagsByWorkflowId, workflowIds, globalWorkflows } = action.payload;
        state.version = WORKFLOW_TAGS_PAYLOAD_VERSION;
        state.updatedAt = new Date().toISOString();
        state.tagsByWorkflowId =
          tagsByWorkflowId && typeof tagsByWorkflowId === 'object'
            ? tagsByWorkflowId
            : {};
        state.workflowIds = Array.isArray(workflowIds)
          ? workflowIds
          : Object.keys(state.tagsByWorkflowId);
        state.globalWorkflows = Array.isArray(globalWorkflows)
          ? globalWorkflows
          : [];
        state.error = null;
      })
      .addCase(fetchWorkflowTagsBootstrap.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : 'Failed to load workflow tags';
      });
  },
});

export const {
  setWorkflowTagsPayload,
  setWorkflowTagsLoading,
  setWorkflowTagsError,
  clearWorkflowTags,
} = workflowTagsSlice.actions;

export default workflowTagsSlice.reducer;
